export type BadgePosition = 'bottom-left' | 'top-left' | 'top-right';

export type GalleryBadgeProps = {
  label: string;
  position: BadgePosition;
  desktopPosition?: BadgePosition;
  variant?: 'gradient';
};

export type GalleryBadgeLabels = {
  sortNew: string;
  sustainable: string;
  embroidery: string;
  ourPick: string;
  template?: string;
};

export type GalleryBadgeFlags = {
  isSpreadBrand?: boolean;
  isNew?: boolean;
  isSustainable?: boolean;
  isEmbroidery?: boolean;
  isTemplate?: boolean;
};

const spreadTopLeftWhiteBadge = (label: string): GalleryBadgeProps => ({
  label,
  position: 'bottom-left',
  desktopPosition: 'top-left',
});

export const getOurPickBadge = (label: string): GalleryBadgeProps => ({
  label,
  position: 'top-left',
});

const getTemplateBadge = (label: string): GalleryBadgeProps => ({
  label,
  position: 'bottom-left',
  desktopPosition: 'top-right',
  variant: 'gradient',
});

export const getGalleryFeatureBadges = (
  { isSpreadBrand, isNew, isSustainable, isEmbroidery, isTemplate }: GalleryBadgeFlags,
  labels: Pick<GalleryBadgeLabels, 'sortNew' | 'sustainable' | 'embroidery' | 'template'>
): GalleryBadgeProps[] => {
  const templateBadge =
    isTemplate && labels.template ? getTemplateBadge(labels.template) : undefined;

  if (isSpreadBrand) {
    return [
      isNew ? spreadTopLeftWhiteBadge(labels.sortNew) : undefined,
      isSustainable ? spreadTopLeftWhiteBadge(labels.sustainable) : undefined,
      templateBadge,
      isEmbroidery
        ? ({ label: labels.embroidery, position: 'bottom-left' } satisfies GalleryBadgeProps)
        : undefined,
    ].filter((badge) => badge !== undefined);
  }

  return [
    isSustainable
      ? ({
          label: labels.sustainable,
          position: 'bottom-left',
          desktopPosition: 'top-right',
        } satisfies GalleryBadgeProps)
      : undefined,
    templateBadge,
    isEmbroidery
      ? ({
          label: labels.embroidery,
          position: 'bottom-left',
          desktopPosition: 'top-right',
        } satisfies GalleryBadgeProps)
      : undefined,
    isNew
      ? ({ label: labels.sortNew, position: 'top-left' } satisfies GalleryBadgeProps)
      : undefined,
  ].filter((badge) => badge !== undefined);
};

/** Badges for the hero product-type image (first model image when design/product order is swapped). */
export const getHeroImageBadges = (
  flags: GalleryBadgeFlags,
  labels: GalleryBadgeLabels,
  { showOurPickOnHeroImage }: { showOurPickOnHeroImage: boolean }
): GalleryBadgeProps[] => [
  ...(showOurPickOnHeroImage && flags.isSpreadBrand ? [getOurPickBadge(labels.ourPick)] : []),
  ...getGalleryFeatureBadges(flags, labels),
];
